import { SessionType } from 'components/document/edit/DocumentEditor/types';
import { QueryAllDocumentRequiredFieldsArgs } from 'gql-types';
import { DocumentSettingParameter } from 'services/documentFields/types';
import { AllDocumentRequiredFields } from 'services/documents/ts/types';
import graph from 'services/graph';
import io from 'services/io';

interface GetEditorData {
    wopi_url: string;
    access_token: string;
}

interface EditorParams {
    type: SessionType;
    entityId: string;
    canEdit?: boolean; // для режиму перегляду потрібно вказати `false`
}

export const getDocumentEditData = ({
    type,
    entityId,
    canEdit = true,
}: EditorParams): Promise<GetEditorData> => {
    return io.post(
        `/editor/`,
        { type, entity_id: entityId, can_edit: canEdit },
        true,
    );
};

interface Reviewer {
    id: string;
    type: string;
}

export interface ReviewSettings {
    reviewers: Reviewer[];
    is_required: boolean;
    is_ordered: boolean;
}

export interface SignerEntity {
    id: string;
    type: 'role' | 'group';
}

export interface Recipient {
    edrpou: string;
    name?: string;
    is_email_hidden: boolean;
    role: 'viewer' | 'signer';
    emails: string[] | null;
}

interface InternalRecipientsSettings {
    is_internal: true;
}

interface ExternalRecipientsSettings {
    is_internal: false;
    is_ordered: boolean;
    recipients: Recipient[];
}

export type RecipientsSettings =
    | InternalRecipientsSettings
    | ExternalRecipientsSettings;

export interface VersionSettings {
    is_versioned: boolean;
}

export interface ViewersSettings {
    add_viewers?: string[];
    replace_viewers?: string[];
    remove_viewers?: string[];
}

export interface TagsReplaceSettings {
    tags_ids: string[];
    new_tags: string[];
}

export type TagsAddSettings = TagsReplaceSettings;

export type TagsRemoveSettings = Omit<TagsReplaceSettings, 'new_tags'>;

/**
 * Використовуємо `replace` чи `tags` (deprecated) для оновлення списку тегів
 * `add` - для додавання тегів - під час мамового оновлення документів - коли не відомо чи є теги в документі
 * `remove` - для видалення тегів - під час мамового оновлення документів - коли не відомо чи є теги в документі
 */
export type TagsSettings = {
    replace?: TagsReplaceSettings;
    add?: TagsAddSettings;
    remove?: TagsRemoveSettings;
    /**
     * @deprecated
     * legacy field, use `replace` instead
     */
    tags?: string[];
};

export interface ParametersSettings {
    update_parameters: DocumentSettingParameter[];
    delete_parameters: string[];
}

export interface SignerSettings {
    parallel_signing: boolean;
    entities: SignerEntity[];
}

export interface UpdateDocumentPayload {
    template_id?: string;
    signers_settings?: SignerSettings;
    recipients_settings?: RecipientsSettings;
    reviews_settings?: ReviewSettings;
    viewers_settings?: ViewersSettings;
    document_settings?: DocumentSettings;
    tags_settings?: TagsSettings;
    parameters_settings?: ParametersSettings;
    version_settings?: VersionSettings;
    /**
     * @deprecated - use `signers_settings` instead
     */
    update_sign_process?: boolean;
    /**
     * @deprecated - use `signers_settings` instead
     */
    parallel_signing?: boolean;
    /**
     * @deprecated - use `signers_settings` instead
     */
    signer_entities?: SignerEntity[];
    /**
     * @deprecated
     */
    update_info?: boolean;
}

export interface DocumentSettings {
    date_document?: Nullable<ISODate | Date>;
    number?: string;
    title?: string;
    category?: string | '0';
    amount?: Nullable<string | number>;
}

export const updateDocument = async (
    docId: string,
    data: UpdateDocumentPayload,
): Promise<void> =>
    io.request('PATCH', `/internal-api/documents/${docId}`, data);

export const getAllDocumentRequiredFields = async ({
    companies_ids,
    edrpous,
}: QueryAllDocumentRequiredFieldsArgs): Promise<
    AllDocumentRequiredFields[]
> => {
    const data = await graph.query({
        query: /* GraphQL */ `
            query AllDocumentRequiredFields(
                $companies_ids: [String!]
                $edrpous: [String!]
            ) {
                allDocumentRequiredFields(
                    edrpous: $edrpous
                    companies_ids: $companies_ids
                ) {
                    id
                    companyId
                    documentCategory
                    isTypeRequired
                    isNumberRequired
                    isDateRequired
                    isAmountRequired
                }
            }
        `,
        variables: { companies_ids, edrpous },
    });
    return data.allDocumentRequiredFields;
};

export const createVersionDraft = async (
    documentId: string,
    versionId: string,
): Promise<void> => {
    return await io.request(
        'POST',
        `/internal-api/drafts/${documentId}/version/${versionId}`,
    );
};

export const convertDraftToNewVersionDocument = async (
    draftId: string,
): Promise<void> => {
    return await io.request('POST', `/internal-api/drafts/${draftId}/version`);
};

export const getDocumentDraftContent = async (
    draftId: string,
): Promise<void> => {
    return await io.request('GET', `/internal-api/drafts/${draftId}/content`);
};

export const deleteVersionDraft = async (draftId: string): Promise<void> => {
    return await io.request('DELETE', `/internal-api/drafts/${draftId}`);
};

export const archiveDocumentsApi = async (docsIds: string[]) =>
    await io.request('POST', '/internal-api/archive/documents', {
        document_ids: docsIds,
    });

export const unArchiveDocumentsApi = async (docsIds: string[]) =>
    await io.request('DELETE', '/internal-api/archive/documents', {
        document_ids: docsIds,
    });
